/**
 * 全局 YouTube API 快取服務
 * 確保每支影片每天只請求一次 YouTube API，節省配額
 */

interface CachedVideoInfo {
  videoId: string;
  youtubeInfo: any;
  lastRequestDate: string; // YYYY-MM-DD 格式
  requestCount: number;
  createdAt: string;
}

interface GlobalCacheStats {
  totalRequests: number;
  cacheHits: number;
  cacheMisses: number;
  quotaSaved: number;
}

class GlobalYoutubeCache {
  private cache: Map<string, CachedVideoInfo> = new Map();
  private pendingRequests: Map<string, Promise<any>> = new Map(); // 🔧 新增：追蹤進行中的請求
  private stats: GlobalCacheStats = {
    totalRequests: 0,
    cacheHits: 0,
    cacheMisses: 0,
    quotaSaved: 0
  };
  private readonly CACHE_KEY = 'global_youtube_cache';
  private readonly STATS_KEY = 'global_youtube_cache_stats';

  constructor() {
    this.loadFromStorage();
    console.log('🎯 全局 YouTube 快取系統已初始化');
  }

  /**
   * 獲取影片資訊，如果今天已經請求過則返回快取，否則調用 API
   * 🔧 新增：防止並發重複請求
   */
  async getVideoInfo(videoId: string, apiCaller: () => Promise<any>): Promise<any> {
    if (!videoId || videoId.length !== 11) {
      throw new Error('無效的 videoId');
    }

    this.stats.totalRequests++;

    const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD
    const cacheKey = `${videoId}_${today}`;
    
    // 檢查今日快取
    const cached = this.cache.get(cacheKey);
    if (cached) {
      console.log(`🎯 快取命中: ${videoId} (今日已請求 ${cached.requestCount} 次)`);
      this.stats.cacheHits++;
      this.stats.quotaSaved++;
      return cached.youtubeInfo;
    }

    // 🔧 新增：檢查是否有進行中的請求
    const pendingRequest = this.pendingRequests.get(videoId);
    if (pendingRequest) {
      console.log(`⏳ 等待進行中的請求: ${videoId}`);
      this.stats.cacheHits++; // 算作快取命中（避免重複請求）
      this.stats.quotaSaved++;
      return pendingRequest;
    }

    // 檢查是否有歷史快取（但過期）
    const historicalKey = this.findHistoricalCache(videoId);
    if (historicalKey) {
      const historical = this.cache.get(historicalKey);
      console.log(`📅 發現歷史快取: ${videoId} (${historical?.lastRequestDate})`);
    }

    console.log(`🔄 首次請求今日 API: ${videoId}`);
    this.stats.cacheMisses++;

    // 🔧 新增：創建並追蹤請求 Promise
    const requestPromise = (async () => {
      try {
        // 調用 YouTube API
        const youtubeInfo = await apiCaller();
        
        // 保存到快取
        const cacheEntry: CachedVideoInfo = {
          videoId,
          youtubeInfo,
          lastRequestDate: today,
          requestCount: 1,
          createdAt: new Date().toISOString()
        };

        this.cache.set(cacheKey, cacheEntry);
        this.saveToStorage();

        console.log(`✅ API 請求成功並已快取: ${videoId}`);
        return youtubeInfo;

      } catch (error) {
        console.error(`❌ API 請求失敗: ${videoId}`, error);
        
        // 如果是配額超限錯誤，記錄但不拋出
        if (error && typeof error === 'object' && 'response' in error) {
          const apiError = error as any;
          if (apiError.response?.status === 403) {
            console.warn('⚠️ YouTube API 配額超限，嘗試返回歷史快取');
            // 嘗試返回任何可用的歷史快取
            const historicalKey = this.findHistoricalCache(videoId);
            if (historicalKey) {
              const historical = this.cache.get(historicalKey);
              if (historical) {
                console.log(`🔄 配額超限，返回歷史快取: ${videoId}`);
                return historical.youtubeInfo;
              }
            }
          }
        }
        
        throw error;
      } finally {
        // 🔧 新增：清除 pending 狀態
        this.pendingRequests.delete(videoId);
      }
    })();

    // 🔧 新增：保存 pending request
    this.pendingRequests.set(videoId, requestPromise);
    
    return requestPromise;
  }

  /**
   * 查找歷史快取（不限日期）
   */
  private findHistoricalCache(videoId: string): string | null {
    for (const [key, value] of this.cache.entries()) {
      if (value.videoId === videoId) {
        return key;
      }
    }
    return null;
  }

  /**
   * 清理過期快取（保留最近7天）
   */
  cleanupExpiredCache(): void {
    const today = new Date();
    const sevenDaysAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
    const sevenDaysAgoStr = sevenDaysAgo.toISOString().split('T')[0];

    let cleanedCount = 0;
    for (const [key, value] of this.cache.entries()) {
      if (value.lastRequestDate < sevenDaysAgoStr) {
        this.cache.delete(key);
        cleanedCount++;
      }
    }

    if (cleanedCount > 0) {
      console.log(`🧹 清理了 ${cleanedCount} 個過期快取項目`);
      this.saveToStorage();
    }
  }

  /**
   * 獲取快取統計
   */
  getStats(): GlobalCacheStats & { cacheSize: number; pendingRequests: number; hitRate: number } {
    const hitRate = this.stats.totalRequests > 0 
      ? (this.stats.cacheHits / this.stats.totalRequests) * 100 
      : 0;

    return {
      ...this.stats,
      cacheSize: this.cache.size,
      pendingRequests: this.pendingRequests.size, // 🔧 新增：顯示進行中的請求數
      hitRate: Math.round(hitRate * 100) / 100
    };
  }

  /**
   * 重置統計
   */
  resetStats(): void {
    this.stats = {
      totalRequests: 0,
      cacheHits: 0,
      cacheMisses: 0,
      quotaSaved: 0
    };
    this.saveStatsToStorage();
  }

  /**
   * 獲取指定影片的快取資訊
   */
  getVideoCacheInfo(videoId: string): CachedVideoInfo | null {
    const today = new Date().toISOString().split('T')[0];
    const cacheKey = `${videoId}_${today}`;
    return this.cache.get(cacheKey) || null;
  }

  /**
   * 從本地存儲載入快取
   */
  private loadFromStorage(): void {
    try {
      const cached = localStorage.getItem(this.CACHE_KEY);
      if (cached) {
        const data = JSON.parse(cached);
        this.cache = new Map(Object.entries(data));
        console.log(`📦 載入了 ${this.cache.size} 個快取項目`);
      }

      const stats = localStorage.getItem(this.STATS_KEY);
      if (stats) {
        this.stats = { ...this.stats, ...JSON.parse(stats) };
      }
    } catch (error) {
      console.warn('⚠️ 載入快取失敗:', error);
    }
  }

  /**
   * 保存快取到本地存儲
   */
  private saveToStorage(): void {
    try {
      const data = Object.fromEntries(this.cache);
      localStorage.setItem(this.CACHE_KEY, JSON.stringify(data));
      this.saveStatsToStorage();
    } catch (error) {
      console.warn('⚠️ 保存快取失敗:', error);
    }
  }

  /**
   * 保存統計到本地存儲
   */
  private saveStatsToStorage(): void {
    try {
      localStorage.setItem(this.STATS_KEY, JSON.stringify(this.stats));
    } catch (error) {
      console.warn('⚠️ 保存統計失敗:', error);
    }
  }

  /**
   * 清除所有快取
   */
  clearAllCache(): void {
    this.cache.clear();
    this.resetStats();
    localStorage.removeItem(this.CACHE_KEY);
    localStorage.removeItem(this.STATS_KEY);
    console.log('🗑️ 已清除所有快取');
  }

  /**
   * 導出快取資料（用於備份）
   */
  exportCache(): string {
    return JSON.stringify({
      cache: Object.fromEntries(this.cache),
      stats: this.stats,
      exportDate: new Date().toISOString()
    });
  }

  /**
   * 導入快取資料（用於還原）
   */
  importCache(data: string): boolean {
    try {
      const parsed = JSON.parse(data);
      if (parsed.cache) {
        this.cache = new Map(Object.entries(parsed.cache));
      }
      if (parsed.stats) {
        this.stats = { ...this.stats, ...parsed.stats };
      }
      this.saveToStorage();
      console.log('📥 快取資料導入成功');
      return true;
    } catch (error) {
      console.error('❌ 快取資料導入失敗:', error);
      return false;
    }
  }
}

// 創建全局實例
export const globalYoutubeCache = new GlobalYoutubeCache();

// 定期清理過期快取（每小時執行一次）
setInterval(() => {
  globalYoutubeCache.cleanupExpiredCache();
}, 60 * 60 * 1000);

// 在頁面卸載時保存快取
window.addEventListener('beforeunload', () => {
  globalYoutubeCache.cleanupExpiredCache();
});

export default globalYoutubeCache;
